import { useMemo, type ReactNode } from 'react'
import { useRequest } from 'alova/client'
import { observer } from 'mobx-react'
import { Spin } from 'antd'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import SubAnalysisDescriptionPopover from '@/pages/ProjectDetailPage/components/VideoAnalysis/SubAnalysisDescriptionPopover/SubAnalysisDescriptionPopover'
import useProductType from '@/helpers/commonHooks/useProductType'
import { api } from '@/api'
import { formatRatioAsPercentValue } from '@/helpers/percent'
import styles from './PortraitStatisticPage.module.less'
import { commonUtils } from '@/helpers/commonUtils'

const SCORE_RANGES = [
  { key: '<40', min: Number.NEGATIVE_INFINITY, max: 40 },
  { key: '40-50', min: 40, max: 50 },
  { key: '50-60', min: 50, max: 60 },
  { key: '60-70', min: 60, max: 70 },
  { key: '70-80', min: 70, max: 80 },
  { key: '80-90', min: 80, max: 90 },
  { key: '90-100', min: 90, max: 100.0001 },
] as const
const CHART_HEIGHT = 350
const CHART_RATIO_DELTA = 0.85

function normalizeNum(num: number | typeof NaN) {
  if (isNaN(num)) {
    return 0
  }
  return num
}

function processPercent(ratio: number, fixed = 1) {
  const percent = (ratio * 100).toFixed(fixed)
  if (percent === '0.0') {
    return '0%'
  }
  return `${percent}%`
}

function formatNorm(norm: number) {
  if (norm === 0) {
    return '-'
  }
  return String(norm)
}

function buildChartData(localScoreList: number[], brandScoreList: number[]) {
  return SCORE_RANGES.map((range) => {
    const localInRange = localScoreList.filter((score) => score >= range.min && score < range.max)
    const brandInRange = brandScoreList.filter((score) => score >= range.min && score < range.max)
    const value = normalizeNum(localInRange.length / localScoreList.length)
    const brandValue = normalizeNum(brandInRange.length / brandScoreList.length)

    return {
      name: range.key,
      value,
      brandValue,
      percent: processPercent(value),
      brandPercent: processPercent(brandValue),
    }
  })
}

function buildChartScale(chartData: Array<{ value: number; brandValue: number }>) {
  const max1 = Math.max(...chartData.map((item) => item.value), 0)
  const max2 = Math.max(...chartData.map((item) => item.brandValue), 0)
  const max = Math.max(max1, max2)
  const rawMaxY = (Math.floor(max * 10) + 1) / 10
  const maxY = rawMaxY > 1 ? 1 : rawMaxY
  const ratio = (CHART_HEIGHT / maxY) * CHART_RATIO_DELTA
  const delta = maxY / 5
  const interval = [
    [0, delta],
    [delta, delta * 2],
    [delta * 2, delta * 3],
    [delta * 3, delta * 4],
    [delta * 4, maxY],
  ]
  return { ratio, interval }
}

function PortraitStatisticPage() {
  const { t } = useTranslation()
  const { productType } = useProductType()
  const { data, loading } = useRequest(api.statistic.getStatistic(productType))

  const statisticData = data?.data
  const scoreResult = data?.scoreResult
  const baseDefault1 = statisticData?.base.default1
  const brandDefault1 = statisticData?.brand.default1
  const brandOpend = statisticData?.brandOpend ?? false
  const regularScoreList = scoreResult?.baseScoreList || []
  const splashScoreList = scoreResult?.brandScoreList || []
  const regularLocalScoreList = scoreResult?.portraitRegularBaseScoreList || regularScoreList
  const regularBrandScoreList = scoreResult?.portraitRegularBrandScoreList || splashScoreList
  const splashLocalScoreList = scoreResult?.portraitSplashBaseScoreList || []
  const splashBrandScoreList = scoreResult?.portraitSplashBrandScoreList || []

  const chartData = useMemo(
    () => buildChartData(regularScoreList, splashScoreList),
    [regularScoreList, splashScoreList],
  )
  const chartScale = useMemo(() => buildChartScale(chartData), [chartData])
  const regularBrandChartData = useMemo(
    () => buildChartData(regularLocalScoreList, regularBrandScoreList),
    [regularLocalScoreList, regularBrandScoreList],
  )
  const splashBrandChartData = useMemo(
    () => buildChartData(splashLocalScoreList, splashBrandScoreList),
    [splashLocalScoreList, splashBrandScoreList],
  )
  const regularBrandChartScale = useMemo(() => buildChartScale(regularBrandChartData), [regularBrandChartData])
  const splashBrandChartScale = useMemo(() => buildChartScale(splashBrandChartData), [splashBrandChartData])
  const baseSummaryDescriptions = useMemo(
    () =>
      brandOpend
        ? [
          {
            title: t('dashboard.dataDetailPage.totalProjects'),
            description: t('dashboard.dataDetailPage.totalProjectsDesc'),
          },
          {
            title: t('dashboard.dataDetailPage.norm'),
            description: t('dashboard.dataDetailPage.localNormDesc'),
          },
          {
            title: t('dashboard.dataDetailPage.projectPassRate'),
            description: t('dashboard.dataDetailPage.projectPassRateDesc'),
          },
        ]
        : [
          {
            title: t('dashboard.dataDetailPage.totalProjects'),
            description: t('dashboard.dataDetailPage.totalProjectsDesc'),
          },
          {
            title: t('dashboard.dataDetailPage.localNorm'),
            description: t('dashboard.dataDetailPage.localNormDesc'),
          },
          {
            title: t('dashboard.dataDetailPage.projectPassRate'),
            description: t('dashboard.dataDetailPage.projectPassRateDesc'),
          },
        ],
    [brandOpend, t],
  )

  return (
    <div className={styles.portraitStatisticPage} data-testid='portrait-statistic-page'>
      <div className={styles.content}>
        {loading && (
          <div className={styles.loading}>
            <Spin />
          </div>
        )}
        {!loading && statisticData && (
          <>
            <div className={styles.summaryTitle}>
              <span>{t('dashboard.dataDetailPage.baseData')}</span>
              <SubAnalysisDescriptionPopover descriptions={baseSummaryDescriptions} />
            </div>
            {brandOpend ? (
              <section className={styles.brandSummarySectionWrap}>
                <section className={styles.brandSummarySection}>
                  <div className={styles.brandSummaryLayout}>
                    <div className={styles.brandSummaryNamesCol}>
                      <div className={styles.brandSummaryNameRow}>
                        <img className={classNames(styles.rowIcon)} src={commonUtils.getImagePath('portraitStatisticPage/normal-project-icon.png')} alt="" />
                        <span>{t('dashboard.portraitDataDetailPage.regularCreative')}</span>
                      </div>
                      <div className={styles.brandSummaryNameRow}>
                        <img className={classNames(styles.rowIcon)} src={commonUtils.getImagePath('portraitStatisticPage/splash-project-icon.png')} alt="" />
                        <span>{t('dashboard.portraitDataDetailPage.splashCreative')}</span>
                      </div>
                    </div>
                    <BrandSummaryDataCol
                      title={t('dashboard.dataDetailPage.normProject')}
                      regularData={statisticData.base.default}
                      splashData={baseDefault1}
                    />
                    <BrandSummaryDataCol
                      title={t('dashboard.dataDetailPage.brandNormProject')}
                      regularData={statisticData.brand.default}
                      splashData={brandDefault1}
                    />
                  </div>
                </section>
              </section>
            ) : (
              <section className={styles.summarySectionWrap}>
                <section className={styles.summarySection}>
                <SummaryRow
                  title={t('dashboard.portraitDataDetailPage.regularCreative')}
                  projects={statisticData.base.default.projects}
                  localNorm={formatNorm(statisticData.base.default.norm)}
                  passRate={formatRatioAsPercentValue(statisticData.base.default.passRatio)}
                  iconVariant='regular'
                />
                  <div className={styles.rowDivider} />
                <SummaryRow
                  title={t('dashboard.portraitDataDetailPage.splashCreative')}
                  projects={baseDefault1?.projects ?? 0}
                  localNorm={formatNorm(baseDefault1?.norm ?? 0)}
                  passRate={formatRatioAsPercentValue(baseDefault1?.passRatio ?? 0)}
                    iconVariant='splash'
                  />
                </section>
              </section>
            )}

            <div className={styles.divider} />

            <section className={styles.chartSection}>
              <div className={styles.sectionTitle}>
                <div className={styles.titleMain}>
                  <span>{t('dashboard.dataDetailPage.advertisingEffectivenessIndexDistribution')}</span>
                  <SubAnalysisDescriptionPopover
                    descriptions={[
                      {
                        title: t('dashboard.dataDetailPage.advertisingEffectivenessIndexDistribution'),
                        description: t('dashboard.dataDetailPage.advertisingEffectivenessIndexDistributionDesc'),
                      },
                    ]}
                  />
                </div>
                <div className={styles.legendGroup}>
                  <LegendItem
                    color='regular'
                    label={brandOpend ? t('dashboard.dataDetailPage.normProject') : t('dashboard.portraitDataDetailPage.regularCreativeProject')}
                  />
                  <LegendItem
                    color='splash'
                    label={brandOpend ? t('dashboard.dataDetailPage.brandNormProject') : t('dashboard.portraitDataDetailPage.splashCreativeProject')}
                  />
                </div>
              </div>

              {brandOpend ? (
                <div className={styles.doubleChartContent}>
                  <DistributionChart
                    title={t('dashboard.portraitDataDetailPage.regularCreativeProject')}
                    chartData={regularBrandChartData}
                    chartScale={regularBrandChartScale}
                  />
                  <DistributionChart
                    title={t('dashboard.portraitDataDetailPage.splashCreativeProject')}
                    chartData={splashBrandChartData}
                    chartScale={splashBrandChartScale}
                    showTopLegend
                  />
                </div>
              ) : (
                <div className={styles.indexAnalysisContent}>
                  <DistributionChart
                    chartData={chartData}
                    chartScale={chartScale}
                  />
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  )
}

function SummaryRow(props: {
  title: string
  projects: number
  localNorm: string
  passRate: string
  iconVariant: 'regular' | 'splash'
}) {
  const { t } = useTranslation()
  return (
    <div className={styles.summaryRow}>
      <div className={styles.rowTitle}>
        {
          props.iconVariant === 'splash'
            ? <img className={styles.rowIcon} src={commonUtils.getImagePath('portraitStatisticPage/splash-project-icon.png')} alt="" />
            : <img className={styles.rowIcon} src={commonUtils.getImagePath('portraitStatisticPage/normal-project-icon.png')} alt="" />
        }
        <span>{props.title}</span>
      </div>
      <div className={styles.metricGrid}>
        <MetricItem label={t('dashboard.portraitDataDetailPage.projects')} value={String(props.projects)} />
        <MetricItem label={t('dashboard.portraitDataDetailPage.localNorm')} value={props.localNorm} />
        <MetricItem
          label={t('dashboard.portraitDataDetailPage.projectPassRate')}
          value={<span>{props.passRate}</span>}
        />
      </div>
    </div>
  )
}

function BrandSummaryDataCol(props: {
  title: string
  regularData?: Models.StatisticNormDetail
  splashData?: Models.StatisticNormDetail
}) {
  const { t } = useTranslation()
  return (
    <div className={styles.brandSummaryDataCol}>
      <div className={styles.brandSummaryDataHeader}>
        <span className={styles.brandSummaryDot} >
          <span className={styles.dot1}></span>
          <span className={styles.dot2}></span>
        </span>
        <span>{props.title}</span>
      </div>
      <div className={styles.brandSummaryDataBody}>
        <div className={styles.brandSummaryMetricsRow}>
          <MetricItem label={t('dashboard.portraitDataDetailPage.projects')} value={String(props.regularData?.projects ?? 0)} />
          <MetricItem label={t('dashboard.dataDetailPage.norm')} value={formatNorm(props.regularData?.norm ?? 0)} />
          <MetricItem
            label={t('dashboard.portraitDataDetailPage.passRate')}
            value={
              <span>
                {formatRatioAsPercentValue(props.regularData?.passRatio ?? 0)}
                <span className={styles.percentSymbol}>%</span>
              </span>
            }
          />
        </div>
        <div className={styles.brandSummaryMetricsRow}>
          <MetricItem label={t('dashboard.portraitDataDetailPage.projects')} value={String(props.splashData?.projects ?? 0)} />
          <MetricItem label={t('dashboard.dataDetailPage.norm')} value={formatNorm(props.splashData?.norm ?? 0)} />
          <MetricItem
            label={t('dashboard.portraitDataDetailPage.passRate')}
            value={
              <span>
                {formatRatioAsPercentValue(props.splashData?.passRatio ?? 0)}
                <span className={styles.percentSymbol}>%</span>
              </span>
            }
          />
        </div>
      </div>
    </div>
  )
}

function MetricItem(props: {
  label: string
  value: ReactNode
}) {
  return (
    <div className={styles.metricItem}>
      <div className={styles.metricValue}>{props.value}</div>
      <div className={styles.metricLabel}>{props.label}</div>
    </div>
  )
}

function LegendItem(props: { label: string; color: 'regular' | 'splash' }) {
  return (
    <div className={styles.legendItem}>
      <span className={classNames(styles.legendDot, props.color === 'splash' && styles.legendDotSplash)} />
      <span>{props.label}</span>
    </div>
  )
}

function DistributionChart(props: {
  title?: string
  chartData: Array<{ name: string; value: number; brandValue: number; percent: string; brandPercent: string }>
  chartScale: { ratio: number; interval: number[][] }
  showTopLegend?: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className={styles.chartPanel}>
      {props.showTopLegend && (
        <div className={styles.chartPanelLegend}>
          <LegendItem
            color='regular'
            label={t('dashboard.dataDetailPage.normProject')}
          />
          <LegendItem
            color='splash'
            label={t('dashboard.dataDetailPage.brandNormProject')}
          />
        </div>
      )}
      <div className={styles.chartBox} style={{ height: CHART_HEIGHT }}>
        <div className={styles.chartBoxContent} style={{ height: CHART_HEIGHT * CHART_RATIO_DELTA }}>
          <div className={styles.chartBg}>
            {props.chartScale.interval.map((item, index) => (
              <div key={index} className={styles.chartBgItem}>
                <span className={styles.chartBgItemValue1}>
                  {processPercent(item[1], 0)}
                </span>
                {index === 0 && (
                  <span className={styles.chartBgItemValue2}>
                    {processPercent(item[0], 0)}
                  </span>
                )}
              </div>
            ))}
          </div>

          <div className={styles.chartContent}>
            {props.chartData.map((item) => (
              <div key={item.name} className={styles.chartItem}>
                <div
                  className={styles.chartItemBar}
                  style={{ height: item.value * props.chartScale.ratio }}
                >
                  <div className={styles.chartItemValue}>{item.percent}</div>
                </div>
                <div
                  className={classNames(styles.chartItemBar, styles.brand)}
                  style={{ height: item.brandValue * props.chartScale.ratio }}
                >
                  <div className={styles.chartItemValue}>{item.brandPercent}</div>
                </div>
                <span className={styles.chartItemName}>{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {props.title && <div className={styles.chartPanelTitle}>{props.title}</div>}
    </div>
  )
}

export default observer(PortraitStatisticPage)
